<script>
import {computed, ref} from "vue";
import useTools from "@/use/Tools";

export default {
    props: {
        items: Array,
        loading: Boolean,
        identity: String,
        search: String,
        type: String,
        readonly: Boolean,
        common: Boolean,
    },
    emits: ["add", "update", "sync"],
    setup(props, {emit}) {
        const {$i18n} = useTools();

        const selectedItem = ref(null);

        const headers = ref([
            {
                text: $i18n.t("worker.code"),
                align: "start",
                sortable: true,
                value: "code",
                width: "0%",
            },
            {
                text: $i18n.t("module.worker"),
                align: "start",
                sortable: true,
                value: "name",
                width: "0%",
            },
            {
                text: "Objectif",
                align: "start",
                sortable: true,
                value: "measureName",
                width: "0%",
            },

            /*{
                text: "Cibles",
                align: "start",
                sortable: true,
                value: "target",
                width: "0%",
            },*/
            {
                text: "PONDERATION",
                align: "start",
                sortable: true,
                value: "gravity",
                width: "0%",
            },
            {
                text: $i18n.t("data_select.WORKER_WORKPLACE"),
                align: "start",
                sortable: true,
                value: "workplaceName",
                width: "0%",
            },
            {
                text: $i18n.t("module.organizational_chart"),
                align: "start",
                sortable: true,
                value: "organizationalChartName",
                width: "0%",
            },
            {
                text: $i18n.t("module.occupation"),
                align: "start",
                sortable: true,
                value: "OccupationName",
                width: "0%",
            },
        ]);

        if (props.type === 'EVALUATION_PERFORMANCE') {
            headers.value.splice(3, 0, {
                text: "KPI/Cible",
                align: "start",
                sortable: true,
                value: "measureKeyPerformanceIndicator",
                width: "0%",
            })
            headers.value.splice(5, 0,  {
                text: $i18n.t("common.deadline"),
                align: "start",
                sortable: true,
                value: "deadline",
                width: "0%",
            })
        }

        if(props.common) {
            const toRemove = ["code", "name", "workplaceName", "organizationalChartName", "OccupationName"];
            headers.value = headers.value.filter(h => !toRemove.includes(h.value));
        }


        const rowsWithNewItem = computed(() => {
            let list = props.items;

            if(props.common) {
                if (!list?.length) return [];

                const randomFileCode = list[Math.floor(Math.random() * list.length)].fileCode
                list =  list.filter(c => c.fileCode === randomFileCode)
            }

            // sort a copy (never mutate props), by code then by order
            list = [...list].sort((a, b) =>
                String(a.code).localeCompare(String(b.code), undefined, { numeric: true }) ||
                (a.order ?? 0) - (b.order ?? 0)
            );

            // your flat list, ordered by code
            const result = [];

            list.forEach((row, i) => {
                result.push(row);

                const isLastOfGroup = !list[i + 1] || list[i + 1].code !== row.code;

                if (isLastOfGroup) {
                    result.push({
                        ...row,              // same attributes as the group
                        id: `new-${i}-${row.code}`,    // unique, stable across recomputations
                        sourceId: row.id,
                        measureName: null,
                        measureNotes: null,
                        gravity: null,
                        target: null,
                        isNew: true            // the flag
                    });
                }
            });

            return result;
        });

        const keys = new WeakMap();
        let keySeq = 0;

        function keyOf(row) {
            if (!keys.has(row)) keys.set(row, ++keySeq);
            return `row-${keys.get(row)}`;
        }

        function clone(obj) {
            return JSON.parse(JSON.stringify(obj));
        }

        function open(item) {
            selectedItem.value = clone(item);
        }

        function save(item) {
            if (!selectedItem.value) return;

            if (selectedItem.value.isNew) {
                emit("add", item);
            } else {
                emit("update", {original: selectedItem.value, item});
                selectedItem.value = clone(item);
            }
        }

        return {
            headers,
            rowsWithNewItem,
            keyOf,
            open,
            save,
        }
    }
}
</script>

<template>
    <div>
        <w-data-table
            :identity="identity"
            :headers="headers"
            :items="rowsWithNewItem"
            :search="search"
            :progress-loading="loading"
            :total="items.length"
            fixed-header
            disable-pagination
            height="600"
        >
            <template v-slot:[`item.code`]="{ item }">
                <span v-if="item.isNew"></span>
                <w-chip
                    v-else
                    :text="item.code"
                />
            </template>
            <template v-slot:[`item.name`]="{ item }">
                <span v-if="item.isNew"></span>
                <w-inline-text
                    v-else
                    bold
                    :text="item.name"
                />
            </template>
            <template v-slot:[`item.workplaceName`]="{ item }">
                <span v-if="item.isNew"></span>
                <w-inline-text
                    v-else
                    :text="item.workplaceName"
                />
            </template>
            <template v-slot:[`item.organizationalChartName`]="{ item }">
                <span v-if="item.isNew"></span>
                <w-inline-text
                    v-else
                    :text="item.organizationalChartName"
                />
            </template>
            <template v-slot:[`item.OccupationName`]="{ item }">
                <span v-if="item.isNew"></span>
                <w-inline-text
                    v-else
                    :text="item.OccupationName"
                />
            </template>
            <template v-slot:[`item.measureName`]="{ item }">
                <w-edit-dialog
                    v-model="item.measureName"
                    v-if="item.isNew && !readonly"
                    :key="keyOf(item)"
                    :place-holder="$t('evaluation.add_criterion')"
                    bold
                    clickable
                    show-place-holder
                    hide-chip
                    large
                    :length="50"
                    @open="open(item)"
                    @save="save(item)"
                />
                <w-edit-dialog
                    v-else
                    v-model="item.measureName"
                    :key="keyOf(item)"
                    hide-chip
                    large
                    :readonly="readonly"
                    :length="50"
                    @open="open(item)"
                    @save="save(item)"
                />

            </template>
            <template v-slot:[`item.measureKeyPerformanceIndicator`]="{ item }">
                <span v-if="item.isNew"></span>
                <w-edit-dialog
                    v-else
                    v-model="item.measureKeyPerformanceIndicator"
                    :key="keyOf(item)"
                    hide-chip
                    large
                    :readonly="readonly"
                    :length="50"
                    @open="open(item)"
                    @save="save(item)"
                />
            </template>
            <template v-slot:[`item.deadline`]="{ item }">
                <span v-if="item.isNew"></span>
                <w-edit-dialog
                    v-else
                    v-model="item.deadline"
                    :key="keyOf(item)"
                    date-format
                    large
                    :readonly="readonly"
                    :length="50"
                    @open="open(item)"
                    @save="save(item)"
                />
            </template>
            <template v-slot:[`item.target`]="{ item }">
                <span v-if="item.isNew"></span>
                <w-edit-dialog
                    v-else
                    v-model="item.target"
                    :key="keyOf(item)"
                    disableChipOutlined
                    chip-color="teal"
                    :readonly="readonly"
                    @open="open(item)"
                    @save="save(item)"
                />
            </template>
            <template v-slot:[`item.gravity`]="{ item }">
                <span v-if="item.isNew"></span>
                <w-edit-dialog
                    v-else
                    v-model="item.gravity"
                    :key="keyOf(item)"
                    chip-color="blue"
                    :readonly="readonly"
                    @open="open(item)"
                    @save="save(item)"
                />
            </template>
        </w-data-table>
    </div>
</template>

<style scoped>

</style>